/* ==========================================================================
   Land Media: Results sections
   - Results showcase: the dashboard tilts from 3D to flat as it scrolls in,
     then the KPI cards pop in one by one (initShowcase in lm-brand.js runs the screens).
   - Results gallery: platform filter tabs (FLIP), masonry grid and an accessible
     lightbox (zoom, pan, pinch, swipe, arrow keys, Esc, focus trap).
   ========================================================================== */
(function () {
  if (window.__lmResults) return;
  window.__lmResults = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(pointer: fine)').matches;
  var canAnimate = !reduceMotion && typeof Element !== 'undefined' && !!Element.prototype.animate;
  var EASE = 'cubic-bezier(.2,.7,.2,1)';

  function once(el, key) {
    if (el['__lmr' + key]) return false;
    el['__lmr' + key] = true;
    return true;
  }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function toArray(list) { return Array.prototype.slice.call(list); }

  /* ======================================================================
     Showcase: scroll-linked tilt + staggered KPI entrance
     ====================================================================== */
  function initShowcase(scene) {
    if (!once(scene, 'Show')) return;
    var show = scene.querySelector('.lm-show');
    if (!show) return;

    function land() {
      if (scene.classList.contains('is-landed')) return;
      scene.classList.add('is-landed');
      show.classList.add('is-in');
    }
    if (reduceMotion) { land(); return; }

    scene.classList.add('is-armed');
    var cur = { p: 0, px: 0, py: 0 }, tgt = { p: 0, px: 0, py: 0 };
    var visible = false, raf = null, first = true;

    function progress() {
      var r = scene.getBoundingClientRect(), vh = window.innerHeight || 800;
      // Fully in view, or the page can't scroll any further (section near the footer): land flat.
      if (r.top < vh && (r.bottom <= vh || window.pageYOffset + vh >= document.documentElement.scrollHeight - 2)) return 1;
      // 0 when the scene's top edge enters the viewport, 1 once it has risen to ~25% from the top.
      return clamp((vh - r.top) / (vh * 0.75), 0, 1);
    }
    function ease(t) { return 1 - (1 - t) * (1 - t); }
    function apply() {
      var mobile = window.innerWidth < 750;
      var p = ease(cur.p), q = 1 - p;
      var rx = q * (mobile ? 16 : 26) - cur.py * 5;
      var ry = q * (mobile ? -6 : -12) + cur.px * 7;
      scene.style.setProperty('--rs-p', p.toFixed(3));
      scene.style.setProperty('--rs-rx', rx.toFixed(2) + 'deg');
      scene.style.setProperty('--rs-ry', ry.toFixed(2) + 'deg');
      scene.style.setProperty('--rs-s', (0.86 + p * 0.14).toFixed(4));
      scene.style.setProperty('--rs-y', (q * (mobile ? 30 : 70)).toFixed(1) + 'px');
      if (cur.p > 0.8) land();
    }
    function frame() {
      raf = null;
      tgt.p = progress();
      if (first) { cur.p = tgt.p; first = false; }
      var k = 0.12;
      cur.p += (tgt.p - cur.p) * k;
      cur.px += (tgt.px - cur.px) * k;
      cur.py += (tgt.py - cur.py) * k;
      apply();
      var settled = Math.abs(tgt.p - cur.p) < 0.001 && Math.abs(tgt.px - cur.px) < 0.002 && Math.abs(tgt.py - cur.py) < 0.002;
      if (!settled) kick();
    }
    function kick() { if (raf === null) raf = requestAnimationFrame(frame); }

    function onScroll() { if (visible) kick(); }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', kick);
    var observers = [];
    scene.__lmrOff = function () {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', kick);
      observers.forEach(function (o) { o.disconnect(); });
      if (raf !== null) cancelAnimationFrame(raf);
      raf = 0; // non-null: kick() becomes a no-op after teardown
    };
    if (finePointer) {
      // Small, restrained tilt towards the cursor once the window has landed.
      show.addEventListener('pointermove', function (e) {
        if (!scene.classList.contains('is-landed')) return;
        var r = show.getBoundingClientRect();
        tgt.px = clamp((e.clientX - r.left) / r.width - 0.5, -0.5, 0.5);
        tgt.py = clamp((e.clientY - r.top) / r.height - 0.5, -0.5, 0.5);
        kick();
      });
      show.addEventListener('pointerleave', function () { tgt.px = 0; tgt.py = 0; kick(); });
    }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { visible = e.isIntersecting; if (visible) kick(); });
      }, { rootMargin: '10% 0px 10% 0px' });
      io.observe(scene);
      // Show the KPI cards at the same moment lm-brand.js's initShowcase starts its count-up / pop
      // (25% of .lm-show visible), so the entrance is seen. The tilt stays scroll-linked.
      var landIo = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) { land(); landIo.disconnect(); } });
      }, { threshold: 0.25 });
      landIo.observe(show);
      observers.push(io, landIo);
    } else {
      visible = true;
      land();
    }
    apply();
    kick();
  }

  /* ======================================================================
     Lightbox (one shared instance, appended to <body>)
     ====================================================================== */
  var ICONS = {
    close: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>',
    prev: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 6-6 6 6 6"/></svg>',
    next: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 6 6 6-6 6"/></svg>',
    zoom: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m20 20-4.8-4.8M10.5 7.5v6M7.5 10.5h6"/></svg>'
  };
  var LB = null;

  function buildLightbox() {
    var root = document.createElement('div');
    root.className = 'lm-vars lm-rlb';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Screenshot viewer');
    root.innerHTML =
      '<div class="lm-rlb__top">' +
        '<p class="lm-rlb__count" aria-live="polite"></p>' +
        '<div class="lm-rlb__tools">' +
          '<button type="button" class="lm-rlb__btn" data-rlb-zoom aria-pressed="false" aria-label="Zoom in">' + ICONS.zoom + '</button>' +
          '<button type="button" class="lm-rlb__btn" data-rlb-close aria-label="Close">' + ICONS.close + '</button>' +
        '</div>' +
      '</div>' +
      '<div class="lm-rlb__view">' +
        '<div class="lm-rlb__frame"></div>' +
        '<span class="lm-rlb__spin" aria-hidden="true"></span>' +
        '<button type="button" class="lm-rlb__nav lm-rlb__nav--prev" data-rlb-prev aria-label="Previous screenshot">' + ICONS.prev + '</button>' +
        '<button type="button" class="lm-rlb__nav lm-rlb__nav--next" data-rlb-next aria-label="Next screenshot">' + ICONS.next + '</button>' +
        '<p class="lm-rlb__hint" aria-hidden="true">Click or scroll to zoom &middot; drag to pan &middot; arrow keys to browse</p>' +
      '</div>' +
      '<aside class="lm-rlb__cap"></aside>';
    document.body.appendChild(root);

    var view = root.querySelector('.lm-rlb__view');
    var frame = root.querySelector('.lm-rlb__frame');
    var count = root.querySelector('.lm-rlb__count');
    var cap = root.querySelector('.lm-rlb__cap');
    var btnZoom = root.querySelector('[data-rlb-zoom]');
    var btnClose = root.querySelector('[data-rlb-close]');
    var btnPrev = root.querySelector('[data-rlb-prev]');
    var btnNext = root.querySelector('[data-rlb-next]');

    var state = { items: [], index: 0, opener: null, owner: null, open: false, media: null, zoomable: false, s: 1, tx: 0, ty: 0, loadId: 0, closeTimer: null };

    /* ----- zoom & pan ----- */
    function bounds() {
      var m = state.media;
      if (!m) return { mx: 0, my: 0 };
      var W = frame.clientWidth, H = frame.clientHeight;
      return { mx: Math.max(0, (m.offsetWidth * state.s - W) / 2), my: Math.max(0, (m.offsetHeight * state.s - H) / 2) };
    }
    function render(animate) {
      var m = state.media;
      if (!m) return;
      var b = bounds();
      state.tx = clamp(state.tx, -b.mx, b.mx);
      state.ty = clamp(state.ty, -b.my, b.my);
      m.style.transition = animate === false ? 'none' : '';
      m.style.transform = state.s === 1 && !state.tx && !state.ty ? '' : 'translate(' + state.tx.toFixed(1) + 'px,' + state.ty.toFixed(1) + 'px) scale(' + state.s.toFixed(3) + ')';
      var zoomed = state.s > 1.01;
      root.classList.toggle('is-zoomed', zoomed);
      btnZoom.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
      btnZoom.setAttribute('aria-label', zoomed ? 'Zoom out' : 'Zoom in');
    }
    // Zoom to scale s, keeping the point (cx, cy) (relative to the frame centre) fixed.
    function zoomTo(s, cx, cy, animate) {
      if (!state.zoomable) return;
      s = clamp(s, 1, 3);
      var dx = (cx - state.tx) / state.s, dy = (cy - state.ty) / state.s;
      state.s = s;
      state.tx = s === 1 ? 0 : cx - s * dx;
      state.ty = s === 1 ? 0 : cy - s * dy;
      render(animate);
    }
    function pointFromEvent(e) {
      var r = frame.getBoundingClientRect();
      return { x: e.clientX - (r.left + r.width / 2), y: e.clientY - (r.top + r.height / 2) };
    }
    // Size the screenshot box to fit the frame from its intrinsic size (data-w/data-h), so tall
    // phone screenshots are never cropped and the preview -> full-res swap keeps the same box.
    function fit() {
      var m = state.media;
      if (!m || !m.__w || !m.__h) return;
      var W = frame.clientWidth, H = frame.clientHeight;
      if (!W || !H) return;
      var k = Math.min(W / m.__w, H / m.__h, 1);
      m.style.width = Math.max(1, Math.round(m.__w * k)) + 'px';
      m.style.height = Math.max(1, Math.round(m.__h * k)) + 'px';
    }

    /* ----- content ----- */
    function show(i, dir) {
      var n = state.items.length;
      if (!n) return;
      state.index = (i + n) % n;
      var li = state.items[state.index];
      var btn = li.querySelector('[data-rgal-open]');
      var tile = li.querySelector('.lm-rgal__tile');
      var full = btn && btn.getAttribute('data-full');
      var loadId = ++state.loadId;

      frame.innerHTML = '';
      state.s = 1; state.tx = 0; state.ty = 0;
      root.classList.remove('is-zoomed', 'is-loading');

      var media;
      if (full) {
        var thumb = li.querySelector('img');
        media = document.createElement('img');
        media.className = 'lm-rlb__media';
        media.alt = btn.getAttribute('data-alt') || '';
        media.draggable = false;
        var w = parseInt(btn.getAttribute('data-w'), 10), h = parseInt(btn.getAttribute('data-h'), 10);
        media.__w = w || (thumb && thumb.naturalWidth) || 0;
        media.__h = h || (thumb && thumb.naturalHeight) || 0;
        if (!media.__w || !media.__h) {
          // No intrinsic size known yet: measure once the first image arrives.
          media.addEventListener('load', function onFirst() {
            media.removeEventListener('load', onFirst);
            if (media.__w && media.__h) return;
            media.__w = media.naturalWidth; media.__h = media.naturalHeight;
            if (state.media === media) { fit(); render(false); }
          });
        }
        media.decoding = 'async';
        var low = thumb && (thumb.currentSrc || thumb.src);
        media.src = low || full;
        if (low && low !== full) {
          root.classList.add('is-loading');
          var hi = new Image();
          hi.onload = function () {
            if (loadId !== state.loadId) return;
            media.src = full;
            root.classList.remove('is-loading');
          };
          hi.onerror = function () { if (loadId === state.loadId) root.classList.remove('is-loading'); };
          hi.src = full;
        }
        state.zoomable = true;
      } else {
        var mock = li.querySelector('.lm-rgal__mock');
        media = mock ? mock.cloneNode(true) : document.createElement('div');
        media.classList.add('lm-rlb__media');
        var plat = (tile && (tile.className.match(/lm-rgal--[a-z]+/) || [])[0]) || '';
        if (plat) media.classList.add(plat);
        state.zoomable = false;
      }
      frame.appendChild(media);
      state.media = media;
      fit();
      btnZoom.disabled = !state.zoomable;

      frame.classList.remove('is-in-next', 'is-in-prev', 'is-in-open');
      if (!reduceMotion) {
        void frame.offsetWidth;
        frame.classList.add(dir > 0 ? 'is-in-next' : dir < 0 ? 'is-in-prev' : 'is-in-open');
      }

      // Caption
      cap.innerHTML = '';
      var tpl = li.querySelector('template.lm-rgal__cap');
      if (tpl && tpl.content) cap.appendChild(tpl.content.cloneNode(true));
      var title = cap.querySelector('.lm-rlb__title');
      if (title) {
        title.id = 'lm-rlb-title';
        root.setAttribute('aria-labelledby', 'lm-rlb-title');
        root.removeAttribute('aria-label');
      } else {
        root.removeAttribute('aria-labelledby');
        root.setAttribute('aria-label', 'Screenshot viewer');
      }
      cap.scrollTop = 0;

      count.textContent = n > 1 ? (state.index + 1) + ' / ' + n : '';
      btnPrev.hidden = btnNext.hidden = n < 2;
      render(false);

      // Warm up the neighbours.
      [state.index + 1, state.index - 1].forEach(function (k) {
        var nb = state.items[(k + n) % n];
        var src = nb && nb.querySelector('[data-rgal-open]') && nb.querySelector('[data-rgal-open]').getAttribute('data-full');
        if (src) { var im = new Image(); im.src = src; }
      });
    }
    function step(d) { if (state.items.length > 1) show(state.index + d, d); }

    function open(items, index, opener, owner) {
      clearTimeout(state.closeTimer);
      state.items = items;
      state.opener = opener;
      state.owner = owner;
      state.open = true;
      document.documentElement.classList.add('lm-rlb-lock');
      root.classList.add('is-open');
      show(index, 0);
      btnClose.focus({ preventScroll: true });
    }
    function close() {
      if (!state.open) return;
      state.open = false;
      root.classList.remove('is-open', 'is-zoomed', 'is-dragging', 'is-loading');
      document.documentElement.classList.remove('lm-rlb-lock');
      state.loadId++;
      state.closeTimer = setTimeout(function () { frame.innerHTML = ''; cap.innerHTML = ''; state.media = null; }, 260);
      var opener = state.opener;
      state.opener = null;
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    }

    /* ----- controls ----- */
    btnClose.addEventListener('click', close);
    btnPrev.addEventListener('click', function () { step(-1); });
    btnNext.addEventListener('click', function () { step(1); });
    btnZoom.addEventListener('click', function () {
      if (state.s > 1.01) zoomTo(1, 0, 0); else zoomTo(2, 0, 0);
    });

    // On document so Esc/arrows still work after a click moved focus to <body>.
    document.addEventListener('keydown', function (e) {
      if (!state.open) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'ArrowRight') { e.preventDefault(); step(1); return; }
      if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); return; }
      if (e.key === '+' || e.key === '=') { zoomTo(state.s + 0.5, 0, 0); return; }
      if (e.key === '-' || e.key === '_') { zoomTo(state.s - 0.5, 0, 0); return; }
      if (e.key === 'Tab') {
        // Focus trap
        var f = toArray(root.querySelectorAll('button:not([disabled]):not([hidden]), a[href], [tabindex]:not([tabindex="-1"])'))
          .filter(function (el) { return el.offsetParent !== null; });
        if (!f.length) return;
        var firstEl = f[0], lastEl = f[f.length - 1];
        if (e.shiftKey && (document.activeElement === firstEl || !root.contains(document.activeElement))) { e.preventDefault(); lastEl.focus(); }
        else if (!e.shiftKey && (document.activeElement === lastEl || !root.contains(document.activeElement))) { e.preventDefault(); firstEl.focus(); }
      }
    });
    // Keep focus inside while open (e.g. after clicking the backdrop).
    document.addEventListener('focusin', function (e) {
      if (state.open && !root.contains(e.target)) btnClose.focus({ preventScroll: true });
    });

    // Wheel zoom around the cursor.
    view.addEventListener('wheel', function (e) {
      if (!state.open || !state.zoomable) return;
      e.preventDefault();
      var pt = pointFromEvent(e);
      var delta = e.deltaMode === 1 ? e.deltaY * 30 : e.deltaY;
      zoomTo(state.s * Math.exp(-delta * 0.0025), pt.x, pt.y, false);
    }, { passive: false });

    // Pointer: tap/click = zoom toggle, drag = pan (zoomed) or swipe (not zoomed), two fingers = pinch.
    var pointers = {}, gesture = null;
    function pointerList() { return Object.keys(pointers).map(function (k) { return pointers[k]; }); }
    view.addEventListener('pointerdown', function (e) {
      if (!state.open || e.target.closest('button')) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      try { view.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      var list = pointerList();
      if (list.length === 2 && state.zoomable) {
        var a = list[0], b = list[1];
        var r = frame.getBoundingClientRect();
        gesture = {
          type: 'pinch', s0: state.s, d0: Math.hypot(a.x - b.x, a.y - b.y) || 1,
          mid: { x: (a.x + b.x) / 2 - (r.left + r.width / 2), y: (a.y + b.y) / 2 - (r.top + r.height / 2) }
        };
      } else if (list.length === 1) {
        // Note the target now: with pointer capture, pointerup always targets the view.
        var onMedia = !!(state.media && (e.target === state.media || state.media.contains(e.target)));
        gesture = { type: 'one', x0: e.clientX, y0: e.clientY, tx0: state.tx, ty0: state.ty, t0: Date.now(), moved: 0, pt: pointFromEvent(e), onMedia: onMedia };
      }
    });
    view.addEventListener('pointermove', function (e) {
      if (!pointers[e.pointerId] || !gesture) return;
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      if (gesture.type === 'pinch') {
        var list = pointerList();
        if (list.length < 2) return;
        var d = Math.hypot(list[0].x - list[1].x, list[0].y - list[1].y);
        zoomTo(gesture.s0 * d / gesture.d0, gesture.mid.x, gesture.mid.y, false);
        return;
      }
      var dx = e.clientX - gesture.x0, dy = e.clientY - gesture.y0;
      gesture.moved = Math.max(gesture.moved, Math.hypot(dx, dy));
      if (state.s > 1.01) {
        root.classList.add('is-dragging');
        state.tx = gesture.tx0 + dx;
        state.ty = gesture.ty0 + dy;
        render(false);
      } else if (state.media && Math.abs(dx) > Math.abs(dy) && state.items.length > 1 && !reduceMotion) {
        state.media.style.transition = 'none';
        state.media.style.transform = 'translateX(' + (dx * 0.4).toFixed(1) + 'px)';
      }
    });
    function endPointer(e) {
      if (!pointers[e.pointerId]) return;
      delete pointers[e.pointerId];
      if (!gesture) return;
      if (gesture.type === 'pinch') {
        if (pointerList().length === 0) gesture = null;
        else gesture = { type: 'idle' };
        if (state.s < 1.05) zoomTo(1, 0, 0);
        return;
      }
      if (gesture.type !== 'one') { if (!pointerList().length) gesture = null; return; }
      var g = gesture;
      gesture = null;
      root.classList.remove('is-dragging');
      var dx = e.clientX - g.x0, dy = e.clientY - g.y0;
      if (e.type === 'pointercancel') { render(); return; }
      if (g.moved < 6 && Date.now() - g.t0 < 500) {
        // Tap / click on the image toggles 2x zoom at that point; on the backdrop it closes.
        if (!g.onMedia) { if (state.s <= 1.01) close(); else zoomTo(1, 0, 0); return; }
        if (!state.zoomable) return;
        if (state.s > 1.01) zoomTo(1, 0, 0); else zoomTo(2, g.pt.x, g.pt.y);
        return;
      }
      if (state.s <= 1.01) {
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.2 && state.items.length > 1) { step(dx < 0 ? 1 : -1); return; }
        render();
      }
    }
    view.addEventListener('pointerup', endPointer);
    view.addEventListener('pointercancel', endPointer);
    window.addEventListener('resize', function () { if (state.open) { fit(); render(false); } });

    return { open: open, close: close, state: state };
  }
  function lightbox() { return LB || (LB = buildLightbox()); }

  /* ======================================================================
     Gallery: filters, masonry, FLIP
     ====================================================================== */
  var ROW = 4; // must match grid-auto-rows in CSS

  function initGallery(sec) {
    if (!once(sec, 'Gallery')) return;
    var grid = sec.querySelector('[data-rgal-grid]');
    if (!grid) return;
    var items = toArray(grid.querySelectorAll(':scope > .lm-rgal__item'));
    var tabs = toArray(sec.querySelectorAll('[data-rgal-filter]'));
    var panel = sec.querySelector('.lm-rgal__panel');
    var status = sec.querySelector('[data-rgal-status]');
    var current = 'all', seq = 0, layoutRaf = null;

    /* ----- masonry ----- */
    function layout() {
      layoutRaf = null;
      var gap = parseFloat(getComputedStyle(grid).getPropertyValue('--gap')) || 22;
      grid.classList.add('is-masonry');
      items.forEach(function (it) {
        if (it.hidden) return;
        var tile = it.firstElementChild;
        if (!tile) return;
        it.style.setProperty('--span', Math.ceil((tile.offsetHeight + gap) / ROW));
      });
    }
    function scheduleLayout() { if (layoutRaf === null) layoutRaf = requestAnimationFrame(layout); }
    layout();
    var dead = false;
    window.addEventListener('resize', scheduleLayout);
    window.addEventListener('load', scheduleLayout);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { if (!dead) scheduleLayout(); });
    var ro = null;
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(scheduleLayout);
      items.forEach(function (it) { if (it.firstElementChild) ro.observe(it.firstElementChild); });
    }
    /* ----- phone carousel: progress bar under the swipeable row ----- */
    var bar = sec.querySelector('[data-rgal-progress]');
    var swipe = sec.querySelector('.lm-rgal__swipe');
    var progRaf = null;
    function progress() {
      progRaf = null;
      if (!bar) return;
      var max = grid.scrollWidth - grid.clientWidth;
      var scrolls = max > 2 && getComputedStyle(grid).display === 'flex';
      if (swipe) swipe.classList.toggle('is-static', !scrolls);
      if (!scrolls) return;
      bar.style.setProperty('--w', Math.max(0.12, grid.clientWidth / grid.scrollWidth).toFixed(3));
      bar.style.setProperty('--p', clamp(grid.scrollLeft / max, 0, 1).toFixed(3));
    }
    function scheduleProgress() { if (progRaf === null) progRaf = requestAnimationFrame(progress); }
    grid.addEventListener('scroll', function () {
      scheduleProgress();
      if (grid.scrollLeft > 20) sec.classList.add('is-swiped');
    }, { passive: true });
    window.addEventListener('resize', scheduleProgress);
    progress();

    sec.__lmrOff = function () {
      dead = true;
      window.removeEventListener('resize', scheduleProgress);
      window.removeEventListener('resize', scheduleLayout);
      window.removeEventListener('load', scheduleLayout);
      if (ro) ro.disconnect();
      if (layoutRaf !== null) cancelAnimationFrame(layoutRaf);
      layoutRaf = 0; // non-null: scheduleLayout() becomes a no-op after teardown
    };

    /* ----- filtering ----- */
    function matches(it, f) { return f === 'all' || it.getAttribute('data-platform') === f; }
    function visibleItems() { return items.filter(function (it) { return !it.hidden; }); }
    function announce() {
      if (!status) return;
      var n = visibleItems().length;
      var tab = tabs.filter(function (t) { return t.getAttribute('data-rgal-filter') === current; })[0];
      var name = tab ? (tab.querySelector('span') || tab).textContent.trim() : '';
      status.textContent = 'Showing ' + n + ' screenshot' + (n === 1 ? '' : 's') + (current === 'all' ? '' : ' for ' + name);
    }
    var tabBar = sec.querySelector('.lm-rgal__filters');
    // Phones show the tabs as one swipeable row: keep the chosen pill in view.
    function revealTab(t) {
      if (!tabBar || tabBar.scrollWidth <= tabBar.clientWidth + 1) return;
      var left = t.offsetLeft - (tabBar.clientWidth - t.offsetWidth) / 2;
      try { tabBar.scrollTo({ left: left, behavior: reduceMotion ? 'auto' : 'smooth' }); } catch (e) { tabBar.scrollLeft = left; }
    }
    function setTabs(f, focus) {
      tabs.forEach(function (t) {
        var on = t.getAttribute('data-rgal-filter') === f;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        if (on && focus) t.focus({ preventScroll: true });
        if (on) revealTab(t);
        if (on && panel && panel.getAttribute('role') === 'tabpanel') panel.setAttribute('aria-labelledby', t.id);
      });
    }
    function cancelAnims(it) {
      if (it.getAnimations) it.getAnimations().forEach(function (a) { if (a instanceof window.Animation && !(window.CSSTransition && a instanceof window.CSSTransition)) a.cancel(); });
    }
    function filter(f, animate) {
      if (f === current && animate) return;
      current = f;
      var my = ++seq;
      announce();
      // Phone carousel: start the filtered set from its first tile.
      if (grid.scrollLeft > 0) { try { grid.scrollTo({ left: 0, behavior: reduceMotion ? 'auto' : 'smooth' }); } catch (e) { grid.scrollLeft = 0; } }
      setTimeout(scheduleProgress, 650);
      if (!animate || !canAnimate) {
        items.forEach(function (it) { cancelAnims(it); it.hidden = !matches(it, f); });
        layout();
        announce();
        return;
      }
      items.forEach(function (it) { cancelAnims(it); it.classList.add('is-in'); });
      var before = new Map();
      visibleItems().forEach(function (it) { before.set(it, it.getBoundingClientRect()); });
      var leaving = items.filter(function (it) { return !it.hidden && !matches(it, f); });
      var entering = items.filter(function (it) { return it.hidden && matches(it, f); });
      var staying = items.filter(function (it) { return !it.hidden && matches(it, f); });

      var pending = leaving.length;
      function phase2() {
        if (my !== seq) return;
        leaving.forEach(function (it) { cancelAnims(it); it.hidden = true; });
        entering.forEach(function (it) { it.hidden = false; });
        layout();
        staying.forEach(function (it) {
          var a = before.get(it), b = it.getBoundingClientRect();
          var dx = a.left - b.left, dy = a.top - b.top;
          if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
          it.animate([{ transform: 'translate(' + dx + 'px,' + dy + 'px)' }, { transform: 'none' }], { duration: 560, easing: EASE });
        });
        entering.forEach(function (it, i) {
          it.animate([
            { opacity: 0, transform: 'translateY(18px) scale(.94)' },
            { opacity: 1, transform: 'none' }
          ], { duration: 460, delay: 90 + i * 55, easing: EASE, fill: 'backwards' });
        });
        announce();
      }
      if (!pending) { phase2(); return; }
      leaving.forEach(function (it) {
        var anim = it.animate([
          { opacity: 1, transform: 'none' },
          { opacity: 0, transform: 'scale(.94)' }
        ], { duration: 190, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards' });
        anim.onfinish = anim.oncancel = function () { if (--pending === 0) phase2(); };
      });
    }
    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () { setTabs(t.getAttribute('data-rgal-filter'), false); filter(t.getAttribute('data-rgal-filter'), true); });
      t.addEventListener('keydown', function (e) {
        var k = -1;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') k = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') k = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') k = 0;
        else if (e.key === 'End') k = tabs.length - 1;
        if (k < 0) return;
        e.preventDefault();
        var key = tabs[k].getAttribute('data-rgal-filter');
        setTabs(key, true);
        filter(key, true);
      });
    });

    /* ----- open in lightbox ----- */
    // Only tiles with an opener ([data-rgal-open]) take part: on the live store, image-less
    // placeholders are static and are skipped by next/prev. The list follows the current filter.
    function openable(it) { return !!it.querySelector('[data-rgal-open]'); }
    function openItem(li, opener) {
      var list = visibleItems().filter(openable);
      var idx = list.indexOf(li);
      if (idx < 0) return;
      lightbox().open(list, idx, opener, sec);
    }
    items.forEach(function (li) {
      var btn = li.querySelector('[data-rgal-open]');
      if (!btn) return;
      btn.addEventListener('click', function () { openItem(li, btn); });
      var tile = li.querySelector('.lm-rgal__tile');
      if (tile) tile.addEventListener('click', function (e) {
        if (e.target.closest('a, button')) return;
        openItem(li, btn);
      });
    });

    sec.__lmrGallery = {
      reveal: function (li) {
        if (li.hidden) { setTabs('all', false); filter('all', false); }
      }
    };
  }

  /* ======================================================================
     Boot
     ====================================================================== */
  function init(root) {
    root = root || document;
    toArray(root.querySelectorAll('[data-lm-rshow]')).forEach(initShowcase);
    toArray(root.querySelectorAll('[data-lm-rgal]')).forEach(initGallery);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  // Theme editor
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    if (LB && LB.state.open && LB.state.owner && e.target.contains(LB.state.owner)) LB.close();
    // Drop window listeners / observers that would otherwise pile up across editor reloads.
    toArray(e.target.querySelectorAll('[data-lm-rshow], [data-lm-rgal]')).forEach(function (el) {
      if (typeof el.__lmrOff === 'function') { el.__lmrOff(); el.__lmrOff = null; }
    });
  });
  document.addEventListener('shopify:block:select', function (e) {
    var li = e.target.closest && e.target.closest('.lm-rgal__item');
    if (!li) return;
    if (LB && LB.state.open) LB.close();
    var sec = li.closest('[data-lm-rgal]');
    if (sec && sec.__lmrGallery) sec.__lmrGallery.reveal(li);
  });
})();
